import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/* eslint-disable @repo/internal/dom-events/no-unsafe-event-listeners */
/** @jsx jsx */
import { useContext, useEffect, useRef } from 'react';
import { jsx } from '@emotion/react';
import { COLLAPSED_LEFT_SIDEBAR_WIDTH, DEFAULT_LEFT_SIDEBAR_WIDTH, FLYOUT_DELAY, RESIZE_BUTTON_SELECTOR, VAR_LEFT_SIDEBAR_FLYOUT, VAR_LEFT_SIDEBAR_WIDTH } from '../../common/constants';
import { getGridStateFromStorage, mergeGridStateIntoStorage, resolveDimension } from '../../common/utils';
import { publishGridState, SidebarResizeContext, useSkipLink } from '../../controllers';
import ResizeControl from '../resize-control';
import LeftSidebarInner from './internal/left-sidebar-inner';
import LeftSidebarOuter from './internal/left-sidebar-outer';
import ResizableChildrenWrapper from './internal/resizable-children-wrapper';
import SlotDimensions from './slot-dimensions';

/**
 * __Left sidebar__
 *
 * Provides a slot for a left sidebar within the PageLayout.
 *
 * - [Examples](https://atlassian.design/components/page-layout/examples)
 * - [Code](https://atlassian.design/components/page-layout/code)
 */
var LeftSidebar = function LeftSidebar(props) {
  var children = props.children,
    width = props.width,
    _props$isFixed = props.isFixed,
    isFixed = _props$isFixed === void 0 ? true : _props$isFixed,
    resizeButtonLabel = props.resizeButtonLabel,
    resizeGrabAreaLabel = props.resizeGrabAreaLabel,
    overrides = props.overrides,
    onExpand = props.onExpand,
    onCollapse = props.onCollapse,
    onResizeStart = props.onResizeStart,
    onResizeEnd = props.onResizeEnd,
    onFlyoutExpand = props.onFlyoutExpand,
    onFlyoutCollapse = props.onFlyoutCollapse,
    testId = props.testId,
    id = props.id,
    skipLinkTitle = props.skipLinkTitle,
    collapsedState = props.collapsedState;
  var flyoutTimerRef = useRef();
  var mouseOverEventRef = useRef();
  var leftSideBarRef = useRef(null);
  var _useContext = useContext(SidebarResizeContext),
    leftSidebarState = _useContext.leftSidebarState,
    setLeftSidebarState = _useContext.setLeftSidebarState;
  var isFlyoutOpen = leftSidebarState.isFlyoutOpen,
    isResizing = leftSidebarState.isResizing,
    flyoutLockCount = leftSidebarState.flyoutLockCount,
    isLeftSidebarCollapsed = leftSidebarState.isLeftSidebarCollapsed,
    leftSidebarWidth = leftSidebarState.leftSidebarWidth,
    lastLeftSidebarWidth = leftSidebarState.lastLeftSidebarWidth;
  var isLocked = flyoutLockCount > 0;
  var isLockedRef = useRef(isLocked);
  var mouseXRef = useRef(0);
  var handlerRef = useRef(null);
  useEffect(function () {
    isLockedRef.current = isLocked;

    // I tried a one-time `mousemove` handler that gets attached
    // when the lock releases. This is not robust because
    // the mouse can stay still after release (e.g. using keyboard)
    // and the sidebar will erroneously stay open.
    //
    // The following solution is likely less performant but more robust.

    if (isLocked && !handlerRef.current) {
      handlerRef.current = function (event) {
        mouseXRef.current = event.clientX;
      };
      document.addEventListener('mousemove', handlerRef.current);
    }
    if (!isLocked && handlerRef.current) {
      if (mouseXRef.current >= lastLeftSidebarWidth) {
        setLeftSidebarState(_objectSpread(_objectSpread({}, leftSidebarState), {}, {
          isFlyoutOpen: false
        }));
      }
      document.removeEventListener('mousemove', handlerRef.current);
      handlerRef.current = null;
    }
    return function () {
      if (handlerRef.current) {
        document.removeEventListener('mousemove', handlerRef.current);
      }
    };
  }, [isLocked, lastLeftSidebarWidth, leftSidebarState, setLeftSidebarState]);
  var _width = Math.max(width || 0, DEFAULT_LEFT_SIDEBAR_WIDTH);
  var collapsedStateOverrideOpen = collapsedState === 'expanded';
  var leftSidebarWidthOnMount;
  if (collapsedStateOverrideOpen) {
    leftSidebarWidthOnMount = resolveDimension(VAR_LEFT_SIDEBAR_FLYOUT, _width, !width);
  } else if (isLeftSidebarCollapsed || collapsedState === 'collapsed') {
    leftSidebarWidthOnMount = COLLAPSED_LEFT_SIDEBAR_WIDTH;
  } else {
    leftSidebarWidthOnMount = resolveDimension(VAR_LEFT_SIDEBAR_WIDTH, _width, !width || !collapsedStateOverrideOpen && getGridStateFromStorage('isLeftSidebarCollapsed'));
  }

  // Update state from cache on mount
  useEffect(function () {
    var cachedCollapsedState = !collapsedStateOverrideOpen && (collapsedState === 'collapsed' || getGridStateFromStorage('isLeftSidebarCollapsed') || false);
    var cachedGridState = getGridStateFromStorage('gridState') || {};
    var leftSidebarWidth = !width && cachedGridState[VAR_LEFT_SIDEBAR_FLYOUT] ? Math.max(cachedGridState[VAR_LEFT_SIDEBAR_FLYOUT], DEFAULT_LEFT_SIDEBAR_WIDTH) : _width;
    var lastLeftSidebarWidth = !width && cachedGridState[VAR_LEFT_SIDEBAR_FLYOUT] ? Math.max(cachedGridState[VAR_LEFT_SIDEBAR_FLYOUT], DEFAULT_LEFT_SIDEBAR_WIDTH) : _width;
    if (cachedCollapsedState) {
      leftSidebarWidth = COLLAPSED_LEFT_SIDEBAR_WIDTH;
    }
    setLeftSidebarState({
      isFlyoutOpen: false,
      isResizing: isResizing,
      isLeftSidebarCollapsed: cachedCollapsedState,
      leftSidebarWidth: leftSidebarWidth,
      lastLeftSidebarWidth: lastLeftSidebarWidth,
      flyoutLockCount: 0,
      isFixed: isFixed
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every time other than mount,
  // update the local storage and css variables.
  var notFirstRun = useRef(false);
  useEffect(function () {
    if (notFirstRun.current) {
      var _publishGridState;
      publishGridState((_publishGridState = {}, _defineProperty(_publishGridState, VAR_LEFT_SIDEBAR_WIDTH, leftSidebarWidth || leftSidebarWidthOnMount), _defineProperty(_publishGridState, VAR_LEFT_SIDEBAR_FLYOUT, lastLeftSidebarWidth), _publishGridState));
      mergeGridStateIntoStorage('isLeftSidebarCollapsed', isLeftSidebarCollapsed);
    }
    if (!notFirstRun.current) {
      notFirstRun.current = true;
    }
    return function () {
      removeMouseOverListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeftSidebarCollapsed, leftSidebarWidth, id]);
  var onMouseOver = function onMouseOver(event) {
    var isMouseOnResizeButton = event.target.matches("[".concat(RESIZE_BUTTON_SELECTOR, "]")) || event.target.matches("[".concat(RESIZE_BUTTON_SELECTOR, "] *"));
    if (isFlyoutOpen || isMouseOnResizeButton || !isLeftSidebarCollapsed) {
      return;
    }
    event.persist();
    flyoutTimerRef.current && clearTimeout(flyoutTimerRef.current);
    if (!mouseOverEventRef.current) {
      mouseOverEventRef.current = function (event) {
        var leftSidebar = leftSideBarRef.current;
        if (leftSidebar === null || isLockedRef.current) {
          return;
        }
        if (!leftSidebar.contains(event.target)) {
          flyoutTimerRef.current && clearTimeout(flyoutTimerRef.current);
          onFlyoutCollapse && onFlyoutCollapse();
          setLeftSidebarState(function (current) {
            return _objectSpread(_objectSpread({}, current), {}, {
              isFlyoutOpen: false
            });
          });
          removeMouseOverListener();
        }
      };
    }
    document.addEventListener('mouseover', mouseOverEventRef.current, {
      capture: true,
      passive: true
    });
    flyoutTimerRef.current = setTimeout(function () {
      setLeftSidebarState(function (current) {
        return _objectSpread(_objectSpread({}, current), {}, {
          isFlyoutOpen: true
        });
      });
      onFlyoutExpand && onFlyoutExpand();
    }, FLYOUT_DELAY);
  };
  var removeMouseOverListener = function removeMouseOverListener() {
    mouseOverEventRef.current && document.removeEventListener('mouseover', mouseOverEventRef.current, {
      capture: true,
      passive: true
    });
  };
  useSkipLink(id, skipLinkTitle);
  var onMouseLeave = function onMouseLeave(event) {
    var isMouseOnResizeButton = event.target.matches("[".concat(RESIZE_BUTTON_SELECTOR, "]")) || event.target.matches("[".concat(RESIZE_BUTTON_SELECTOR, "] *"));
    if (isMouseOnResizeButton || !isLeftSidebarCollapsed || flyoutLockCount > 0) {
      return;
    }
    onFlyoutCollapse && onFlyoutCollapse();
    setTimeout(function () {
      setLeftSidebarState(function (current) {
        return _objectSpread(_objectSpread({}, current), {}, {
          isFlyoutOpen: false
        });
      });
    }, FLYOUT_DELAY);
  };
  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    jsx(LeftSidebarOuter, {
      ref: leftSideBarRef,
      testId: testId,
      onMouseOver: onMouseOver,
      onMouseLeave: onMouseLeave,
      id: id,
      isFixed: isFixed,
      isFlyoutOpen: isFlyoutOpen
    }, jsx(SlotDimensions, {
      variableName: VAR_LEFT_SIDEBAR_WIDTH,
      value: notFirstRun.current ? leftSidebarWidth : leftSidebarWidthOnMount
    }), jsx(LeftSidebarInner, {
      isFixed: isFixed,
      isFlyoutOpen: isFlyoutOpen
    }, jsx(ResizableChildrenWrapper, {
      isFlyoutOpen: isFlyoutOpen,
      isLeftSidebarCollapsed: isLeftSidebarCollapsed,
      hasCollapsedState: !notFirstRun.current && collapsedState === 'collapsed'
    }, children), jsx(ResizeControl, {
      testId: testId,
      resizeGrabAreaLabel: resizeGrabAreaLabel,
      resizeButtonLabel: resizeButtonLabel
      // eslint-disable-next-line @repo/internal/react/no-unsafe-overrides
      ,
      overrides: overrides,
      onCollapse: onCollapse,
      onExpand: onExpand,
      onResizeStart: onResizeStart,
      onResizeEnd: onResizeEnd,
      leftSidebarState: leftSidebarState,
      setLeftSidebarState: setLeftSidebarState
    })))
  );
};
export default LeftSidebar;